import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { ReviewCancelComponent } from '../../components/review-cancel/review-cancel.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { ReviewRequestComponent } from '../../components/review-request/review-request.component';
import { AssessTeamComponent } from '../../components/assess-team/assess-team.component';
import { LucideAngularModule } from 'lucide-angular';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Icons } from '../../../shared/helpers/icons';
import { EStage, EStatus, EYesNo } from '../../../shared/models/types.enum';
import { IAssessCase } from '../../../shared/models/assessCase';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { AssessCaseService } from '../../../shared/services/assessCase.service';
import { FormUtils } from '../../../shared/helpers/formUtils';
import { Router } from '@angular/router';
import { IRoarAssignmentForm } from '../../../shared/models/roarAssignmentForm';
import { RoarAssignmentFormService } from '../../../shared/services/roarAssignmentForm.service';
import { RoarModalComponent } from '../../components/roar-modal/roar-modal.component';
import { FormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { IThreatAssessmentTeam } from '../../../shared/models/contact';
declare var initFlowbite: any;

@Component({
  selector: 'interventions-dashboard',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    LucideAngularModule,
    AssessTeamComponent,
    ReviewRequestComponent,
    SpinnerComponent,
    ReviewCancelComponent,
    PopupMessageComponent,
    RoarModalComponent,
    NgxPaginationModule
  ],
  templateUrl: './interventions-dashboard.component.html',
  styleUrl: './interventions-dashboard.component.css'
})
export class InterventionsDashboardComponent implements OnInit {

  icons = Icons;
  stageEnum = EStage;
  statusEnum = EStatus;
  yesNo = EYesNo;
  currentBuildingId: string | null = null;
  currentUser: IThreatAssessmentTeam | null = null;
  searchQuery: string = '';
  interventionCases: IAssessCase[] = [];
  filteredInterventionCases: IAssessCase[] = [];
  selectedCase: IAssessCase | null = null;
  roarAssignments: IRoarAssignmentForm[] = [];
  messageModal: string | null = null;
  popupMessage: string | null = null;
  page = 1;
  itemsPerPage = 10;

  sortOrder: boolean = true;
  showTeam: boolean = false;
  showRoarModal: boolean = false;
  showReviewRequest: boolean = false;
  showReviewModal: boolean = false;
  showCancelReview: boolean = false;
  loading: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private userDataService: UserDataService,
    private assessCaseService: AssessCaseService,
    private formUtils: FormUtils,
    private roarAssignmentFormService: RoarAssignmentFormService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getCurrentUser();
    this.loadInterventionCases();
  }

  loadInterventionCases(): void {
    // get all case and filter by current stage at first loading
    if (this.currentBuildingId) {
      this.loading = true;
      this.assessCaseService.getBuildingAssessCases(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.message) {
            this.interventionCases = response.filter((item: IAssessCase) => item.current_stage === EStage.Interventions);
            if (isPlatformBrowser(this.platformId)) { // refresh flowbite effects (tooltip) only in browser
              setTimeout(() => {
                initFlowbite();
              }, 100);
            }
            this.filteredInterventionCases = this.interventionCases.length > 0 ? this.interventionCases.reverse() : [];
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching building interventions cases:', error);
          this.popupMessage = error.message
          this.messageModal = 'unsuccess';
        }
      });
    } else {
      return
    }
  }

  sortList(key: keyof typeof this.filteredInterventionCases[0]): void {
    this.sortOrder = !this.sortOrder;
    this.filteredInterventionCases.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  filterAssessCases(): void {
    this.filteredInterventionCases = this.interventionCases.filter((caseItem) => {
      return (
        caseItem.case_id.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.detail_of_situation?.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.created_by_name?.toLowerCase()
          .includes(this.searchQuery.toLowerCase())
      );
    });
  }

  openModal(type: string, clickedCase?: IAssessCase): void {
    if (type === 'team') {
      this.showTeam = true;
    } else if (type === 'review request') {
      this.showReviewRequest = true;
    } else if (type === 'review modal') {
      this.showReviewModal = true;
    } else if (type === 'cancel review') {
      this.showCancelReview = true;
    } else if (type === 'roar') {
      this.showRoarModal = true;
    }
    this.selectedCase = clickedCase ? clickedCase : null;
  }

  closeModal(type: string): void {
    if (type === 'team') {
      this.showTeam = false;
    } else if (type === 'review request') {
      this.showReviewRequest = false;
    } else if (type === 'review modal') {
      this.showReviewModal = false;
    } else if (type === 'cancel review') {
      this.showCancelReview = false;
    } else if (type === 'roar') {
      this.showRoarModal = false;
    }
    this.selectedCase = null;
  } 
  
  handleSave(message: {type: string, message: string}, actionType?: string): void {
    if (actionType === 'team') {
      this.closeModal('team');
    } else if (actionType === 'review request') {
      this.closeModal('review request');
    } else if (actionType === 'cancel review') {
      this.closeModal('cancel review');
    }
    if (message.type === 'success') {
      this.loadInterventionCases();
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'failed') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }

  isStageReviewPending(clickedCase: IAssessCase): boolean {
    if (clickedCase.threat_assessment_summary === this.statusEnum.ReviewPending || clickedCase.roar_assignment_form === EStatus.ReviewPending) {
      return true;
    }
    return false;
  }

  isAllRequiredFormsUndone(selectedCase: IAssessCase): boolean {
    if (selectedCase.threat_assessment_team_status === this.statusEnum.FormUndone || 
        selectedCase.threat_assessment_summary === this.statusEnum.FormUndone) 
    {
      return true;
    }   
    return false;
  }

  isUserInTeam(selectedCase: IAssessCase): boolean {
    return this.currentUser?.case_ids?.includes(selectedCase.case_id) ?? false;
  }
  
  goToPage(pageName: string, caseId: string, roarId?: string): void {
    this.router.navigate(['/interventions/' + pageName + '/' + caseId + (roarId ? '/' + roarId : '')]);
  }

  getTooltip(fieldValue: number, context: string) {
    return this.formUtils.getTooltipContent(fieldValue, context);
  }  
  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
